import { useState } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { FONT_FAMILIES, FONT_WEIGHTS, HEADER_COLORS } from "@/lib/typographyConstants";

// Absolute pt values every per-category size selector shares. Individual
// categories cap the list at a lower max via CATEGORY_SIZE_MAX below —
// keeps the dropdown short and prevents users from picking absurd sizes
// that break layout.
const CAT_FONT_SIZES_ALL = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 26, 28, 30, 32];

// Per-user spec iter 45:
//   • Resume Name → up to 32 pt (display headline).
//   • Section Headers → up to 18 pt.
//   • All other categories (every description flavour, Contact, Job
//     Title, Company, Date, Location, Work Description, Skills,
//     Education, Projects, Certifications, Additional) → up to 14 pt.
const CATEGORY_SIZE_MAX = {
  resumeName: 32,
  sectionHeaders: 18,
  // Everything else falls through to the default:
};
const DEFAULT_CAT_MAX = 14;

const getSizesForCategory = (catKey) =>
  CAT_FONT_SIZES_ALL.filter((n) => n <= (CATEGORY_SIZE_MAX[catKey] ?? DEFAULT_CAT_MAX));

// "Inherit" as a family option means "use the global fallback" — either the
// Header font (for name / section headers / job title / company) or the
// Description font (for description / skills / education / …). This keeps
// per-category families opt-in.
const INHERIT = "inherit";

// Category groupings shown in the panel. Each category has a stable `key`
// (used for React state), a friendly `label`, and a controls-mapping
// consumed at render time. Grouped in a visual hierarchy: Header → Body →
// Experience so users can scan predictably.
const CATEGORY_GROUPS = [
  {
    heading: "Header",
    items: [
      { key: "resumeName",     label: "Resume Name" },
      { key: "contactInfo",    label: "Contact Information" },
      { key: "sectionHeaders", label: "Section Headers" },
    ],
  },
  {
    heading: "Body Text",
    items: [
      { key: "description",    label: "Professional Summary" },
      { key: "skills",         label: "Skills" },
      { key: "education",      label: "Education" },
      { key: "projects",       label: "Projects" },
      { key: "certifications", label: "Certifications" },
      { key: "additional",     label: "Languages · Additional" },
    ],
  },
  {
    heading: "Work Experience",
    items: [
      { key: "company",         label: "Company Name" },
      { key: "jobTitle",        label: "Job Title" },
      { key: "workDescription", label: "Description" },
      { key: "date",            label: "Date" },
      { key: "location",        label: "Location" },
    ],
  },
];

// ---------------- helpers to read/write category values -------------------
// Categories live across a few different bags of props on Builder.js so we
// centralise the read/write dance here to keep the JSX terse.
const useCategoryAdapter = (props) => {
  const bt = props.bodyTypography || {};

  const readers = {
    resumeName: () => ({
      family: props.resumeNameFontFamily,
      size:   props.resumeNameFontSize,
      weight: props.h1FontWeight,
      color:  props.h1Color,
    }),
    sectionHeaders: () => ({
      family: props.sectionHeaderFontFamily,
      size:   props.sectionHeaderFontSize,
      weight: props.h2FontWeight,
      color:  props.h2Color,
    }),
    description: () => ({
      family: props.descriptionFontFamily === "Inter" ? INHERIT : props.descriptionFontFamily,
      size:   props.descriptionFontSize,
      weight: props.descriptionFontWeight,
      color:  props.descriptionColor,
    }),
    contactInfo: () => ({
      family: props.contactInfoFontFamily,
      size:   props.contactInfoFontSize,
      weight: props.contactInfoFontWeight,
      color:  props.contactInfoColor,
    }),
    workDescription: () => ({
      family: props.workDescriptionFontFamily,
      size:   props.workDescriptionFontSize,
      weight: props.workDescriptionFontWeight,
      color:  props.workDescriptionColor,
    }),
    date: () => ({
      family: props.dateFontFamily,
      size:   props.dateFontSize,
      weight: props.dateFontWeight,
      color:  props.dateColor,
    }),
    location: () => ({
      family: props.locationFontFamily,
      size:   props.locationFontSize,
      weight: props.locationFontWeight,
      color:  props.locationColor,
    }),
    jobTitle: () => ({
      family: props.jobTitleFontFamily,
      size:   props.jobTitleFontSize,
      weight: props.jobTitleFontWeight,
      color:  props.jobTitleColor,
    }),
    company: () => ({
      family: props.companyFontFamily,
      size:   props.companyFontSize,
      weight: props.companyFontWeight,
      color:  props.companyColor,
    }),
    skills:         () => bt.skills || {},
    education:      () => bt.education || {},
    projects:       () => bt.projects || {},
    certifications: () => bt.certifications || {},
    additional:     () => bt.additional || {},
  };

  const writers = {
    resumeName: {
      family: props.setResumeNameFontFamily,
      size:   props.setResumeNameFontSize,
      weight: props.setH1FontWeight,
      color:  props.setH1Color,
    },
    sectionHeaders: {
      family: props.setSectionHeaderFontFamily,
      size:   props.setSectionHeaderFontSize,
      weight: props.setH2FontWeight,
      color:  props.setH2Color,
    },
    description: {
      family: (v) => props.setDescriptionFontFamily(v === INHERIT ? "Inter" : v),
      size:   props.setDescriptionFontSize,
      weight: props.setDescriptionFontWeight,
      color:  props.setDescriptionColor,
    },
    contactInfo: {
      family: props.setContactInfoFontFamily,
      size:   props.setContactInfoFontSize,
      weight: props.setContactInfoFontWeight,
      color:  props.setContactInfoColor,
    },
    workDescription: {
      family: props.setWorkDescriptionFontFamily,
      size:   props.setWorkDescriptionFontSize,
      weight: props.setWorkDescriptionFontWeight,
      color:  props.setWorkDescriptionColor,
    },
    date: {
      family: props.setDateFontFamily,
      size:   props.setDateFontSize,
      weight: props.setDateFontWeight,
      color:  props.setDateColor,
    },
    location: {
      family: props.setLocationFontFamily,
      size:   props.setLocationFontSize,
      weight: props.setLocationFontWeight,
      color:  props.setLocationColor,
    },
    jobTitle: {
      family: props.setJobTitleFontFamily,
      size:   props.setJobTitleFontSize,
      weight: props.setJobTitleFontWeight,
      color:  props.setJobTitleColor,
    },
    company: {
      family: props.setCompanyFontFamily,
      size:   props.setCompanyFontSize,
      weight: props.setCompanyFontWeight,
      color:  props.setCompanyColor,
    },
  };
  // Body-category writer shorthand — every write funnels through the same
  // updater so state stays consistent.
  ["skills", "education", "projects", "certifications", "additional"].forEach((k) => {
    writers[k] = {
      family: (v) => props.updateBodyCategory(k, { family: v }),
      size:   (v) => props.updateBodyCategory(k, { size:   v }),
      weight: (v) => props.updateBodyCategory(k, { weight: v }),
      color:  (v) => props.updateBodyCategory(k, { color:  v }),
    };
  });

  return { readers, writers };
};

export default function TypographyPanel(props) {
  const { readers, writers } = useCategoryAdapter(props);
  // Accordion state — only one category expanded at a time keeps the panel
  // scannable when all 10 categories are visible.
  const [openKey, setOpenKey] = useState(null);
  const toggle = (key) => setOpenKey((c) => (c === key ? null : key));

  return (
    <div className="mt-5" data-testid="typography-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">Typography · per category</div>
      </div>
      <div className="text-[10px] text-neutral-500 mb-3 leading-snug">
        Style every resume element independently — click any row to expand its Font Family, Font Size, Font Weight and Font Colour controls. "Inherit" uses the global Header / Description font as a fallback.
      </div>

      {CATEGORY_GROUPS.map((group) => (
        <div key={group.heading} className="mb-3 last:mb-0">
          <div className="text-[9px] font-mono uppercase tracking-widest text-neutral-400 mb-1.5">
            {group.heading}
          </div>
          <div className="border border-black/10">
            {group.items.map((cat, i) => {
              const val = readers[cat.key]();
              const w = writers[cat.key];
              const isOpen = openKey === cat.key;
              return (
                <div key={cat.key} className={`${i > 0 ? "border-t border-black/10" : ""}`} data-testid={`typography-${cat.key}-group`}>
                  <button
                    type="button"
                    onClick={() => toggle(cat.key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
                    data-testid={`typography-${cat.key}-toggle`}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full ring-1 ring-black/10"
                        style={{ background: val.color || "#0a0a0a" }}
                        aria-hidden="true"
                      />
                      <span className="text-xs font-mono uppercase tracking-wider">{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-neutral-500">
                        {val.size}pt · {val.weight}
                      </span>
                      {isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="p-3 bg-neutral-50/50 border-t border-black/10 space-y-3" data-testid={`typography-${cat.key}-body`}>
                      {/* Font family — with "Inherit" as first option so
                          per-category families are opt-in. */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Font family</div>
                        <select
                          value={val.family || INHERIT}
                          onChange={(e) => w.family(e.target.value)}
                          className="w-full h-8 text-xs font-mono uppercase tracking-widest border border-black/10 bg-white px-2 focus:outline-none focus:border-neutral-950 hover:bg-neutral-100 transition-colors"
                          data-testid={`typography-${cat.key}-family-select`}
                          aria-label={`${cat.label} font family`}
                        >
                          <option value={INHERIT} data-testid={`typography-${cat.key}-family-option-inherit`}>Inherit (global)</option>
                          {FONT_FAMILIES.map((f) => (
                            <option key={f.family} value={f.family} data-testid={`typography-${cat.key}-family-option-${f.family.toLowerCase().replace(/\s+/g, "-")}`}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Size</div>
                          <select
                            value={val.size}
                            onChange={(e) => w.size(parseInt(e.target.value, 10))}
                            className="w-full h-8 text-xs font-mono uppercase tracking-widest border border-black/10 bg-white px-2 focus:outline-none focus:border-neutral-950 hover:bg-neutral-100 transition-colors"
                            data-testid={`typography-${cat.key}-size-select`}
                            aria-label={`${cat.label} font size`}
                          >
                            {getSizesForCategory(cat.key).map((s) => (
                              <option key={s} value={s} data-testid={`typography-${cat.key}-size-option-${s}`}>{s} pt</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Weight</div>
                          <select
                            value={val.weight}
                            onChange={(e) => w.weight(parseInt(e.target.value, 10))}
                            className="w-full h-8 text-xs font-mono uppercase tracking-widest border border-black/10 bg-white px-2 focus:outline-none focus:border-neutral-950 hover:bg-neutral-100 transition-colors"
                            data-testid={`typography-${cat.key}-weight-select`}
                            aria-label={`${cat.label} font weight`}
                          >
                            {FONT_WEIGHTS.map((wo) => (
                              <option key={wo.id} value={wo.id} data-testid={`typography-${cat.key}-weight-option-${wo.id}`}>{wo.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Colour</div>
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`typography-${cat.key}-color-swatches`}>
                          {HEADER_COLORS.map((c) => {
                            const active = (val.color || "").toLowerCase() === c.value.toLowerCase();
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => w.color(c.value)}
                                title={c.label}
                                aria-label={`${cat.label} colour ${c.label}`}
                                data-testid={`typography-${cat.key}-color-${c.id}`}
                                className={`w-6 h-6 rounded-full transition-all ${
                                  active ? "ring-2 ring-offset-2 ring-neutral-950 scale-110" : "ring-1 ring-black/10 hover:scale-105"
                                }`}
                                style={{ background: c.value }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
